import { cn } from "@babackapp/customer-ui/lib/utils";
import { StoreIcon } from "lucide-react";
import { motion, Variants } from "motion/react";
import { useState } from "react";

const headerVariants: Variants = {
  hidden: {
    transition: {
      staggerChildren: 0.025,
      when: "afterChildren",
      duration: 0.2,
    },
  },
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 5,
    transition: { duration: 0.2, ease: [0.7, 0, 0.84, 0] },
  }, // expo in
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1], // expo out
    },
  },
};

export function Header({
  className,
  ...props
}: React.ComponentProps<typeof motion.div>) {
  return (
    <motion.div
      variants={headerVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      className={cn(
        "text-center gap-3 flex flex-col items-center justify-center",
        className,
      )}
      {...props}
    />
  );
}

export function HeaderTitle({
  className,
  ...props
}: React.ComponentProps<typeof motion.h3>) {
  return (
    <motion.h3
      variants={itemVariants}
      className={cn(
        "text-xl font-sans inline-flex gap-1 items-center",
        className,
      )}
      {...props}
    />
  );
}

// Shop logos come in any aspect ratio, so the box is bounded on both axes and
// the image fits inside it: a landscape logo grows until it hits the width, a
// portrait one until it hits the height. Tune the whole widget from here.
const SHOP_LOGO_BOX = "max-h-14 max-w-[190px]";
// Fallback mark, sized to sit at roughly the same visual weight.
const SHOP_LOGO_FALLBACK_SIZE = "size-10";

export function ShopLogo({
  className,
  src,
  ...props
}: React.ComponentProps<typeof motion.img>) {
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <motion.div
        layoutId="shop-logo"
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        className={cn("my-2", className)}
      >
        <StoreIcon
          className={cn(SHOP_LOGO_FALLBACK_SIZE, "text-muted-foreground")}
        />
      </motion.div>
    );
  }

  return (
    <motion.img
      layoutId="shop-logo"
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      className={cn(
        "my-2 h-auto w-auto object-contain",
        SHOP_LOGO_BOX,
        className,
      )}
      src={src}
      onError={() => setError(true)}
      {...props}
    />
  );
}

export function HeaderDescription({
  className,
  ...props
}: React.ComponentProps<typeof motion.p>) {
  return (
    <motion.p
      variants={itemVariants}
      className={cn(
        "text-muted-foreground/80 text-sm max-w-[400px] font-content font-normal text-balance",
        className,
      )}
      {...props}
    />
  );
}

export function SectionHeader({
  className,
  ...props
}: React.ComponentProps<typeof motion.span>) {
  return (
    <motion.span
      initial={{ x: -20, opacity: 0 }}
      animate={{
        x: 0,
        opacity: 1,
        transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
      }}
      exit={{
        x: -20,
        opacity: 0,
        transition: { duration: 0.2, ease: [0.7, 0, 0.84, 0] },
      }}
      className={cn("font-medium text-xs text-muted-foreground", className)}
      {...props}
    />
  );
}
